import React, { useMemo } from 'react'
import { createColumnHelper } from '@tanstack/react-table'
import { Box } from '@mui/material'

import { useGetOperationRequests } from '@/services'
import { OperationRequest } from '@/types.ts'
import { FetchErrorMessage } from '@/components'
import { TableComponent } from '@/components/table'
import { useIsMobile } from '@/hooks'
import { useAppDispatch, useAppSelector } from '@/stores/hooks.ts'
import { userSelectors } from '@/stores/slices/user-slice.ts'
import { DefaultFiltersState } from '@/stores'
import { operationRequestsActions, operationRequestsSelectors } from '@/stores/slices/operation-requests-slice.ts'

import {
  getColumnsSettings,
  getStaticColumns,
  defaultSettings,
  KEY_PAGE,
  PartialOperation,
} from './default-settings.tsx'

const columnHelper = createColumnHelper<OperationRequest>()
const defaultColumns = getColumnsSettings(columnHelper)
const staticColumns = getStaticColumns(columnHelper)

export const OperationRequestsTable = (): React.ReactElement => {
  const { isLoading, data, isError, refetch, isFetching } = useGetOperationRequests()
  const settings = useAppSelector((state) => userSelectors.personalSettings(state, KEY_PAGE))
  const isMobile = useIsMobile()
  const dispatch = useAppDispatch()

  const { page, showCount } = useAppSelector(operationRequestsSelectors.filters)

  const handlePaginationParam = (param: keyof DefaultFiltersState) => (value: number) =>
    dispatch(
      operationRequestsActions.setFilter({
        filterKey: param,
        filterValue: value,
      })
    )

  const operationsRequestCols = useMemo(() => {
    const format = isMobile ? 'mobile' : 'desktop'

    const desktopSettings = (settings?.[format] ?? Object.keys(defaultSettings)) as Array<keyof PartialOperation>

    const dynamicSettings = desktopSettings.map((e) => defaultColumns[e])
    return [...dynamicSettings, ...Object.values(staticColumns)]
  }, [settings, isMobile])

  return (
    <FetchErrorMessage isError={isError} refetch={refetch} isFetching={isFetching}>
      <Box mt={3}>
        <TableComponent<OperationRequest>
          rowKey="id"
          isLoading={isLoading}
          columns={operationsRequestCols}
          page={page}
          size={showCount}
          totalPages={data?.data.totalPages}
          columnPinning={{ right: ['send-url-action'] }}
          data={data?.data?.content ?? []}
          onPageChange={handlePaginationParam('page')}
          onSizeChange={handlePaginationParam('showCount')}
        />
      </Box>
    </FetchErrorMessage>
  )
}
